import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/Input';
import { api, ApiException } from '@/api/client';
import type { SpendLogV2Entry } from '@litellm-ui/shared';

/**
 * A normalized log row as returned by the worker.
 *
 * The worker fills in `timestamp` (from upstream startTime), `cost`
 * (from upstream spend), `latency_ms` (from upstream request_duration_ms),
 * and maps upstream `failure` to `error`.
 */
type LogRow = SpendLogV2Entry & {
  timestamp?: string | null;
  cost?: number | null;
  latency_ms?: number | null;
  status?: 'success' | 'error' | null;
};

function todayUtcDateString(): string {
  const now = new Date();
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(now.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function LogsPage() {
  const navigate = useNavigate();
  // LiteLLM /spend/logs/v2 requires both start_date and end_date; default to
  // today in UTC so the page is useful on first open.
  const [filters, setFilters] = useState(() => ({
    start_date: todayUtcDateString(),
    end_date: todayUtcDateString(),
    model: '',
    user_id: '',
    status: '' as '' | 'success' | 'error',
  }));

  // `api<T>()` unwraps `body.data`, so the returned value is the row array
  // directly — same pattern as useUsers/useKeys/useTeams.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['logs', filters],
    queryFn: () =>
      api<LogRow[]>('/api/logs', {
        query: {
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          model: filters.model || undefined,
          user_id: filters.user_id || undefined,
          status: filters.status || undefined,
          page: 1,
          page_size: 100,
        },
      }),
  });

  const rows = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Logs</h1>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Input
          type="date"
          value={filters.start_date}
          onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
          style={{ width: 160 }}
        />
        <Input
          type="date"
          value={filters.end_date}
          onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
          style={{ width: 160 }}
        />
        <Input
          placeholder="Model"
          value={filters.model}
          onChange={(e) => setFilters({ ...filters, model: e.target.value })}
          style={{ width: 160 }}
        />
        <Input
          placeholder="User"
          value={filters.user_id}
          onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
          style={{ width: 160 }}
        />
        <select
          value={filters.status}
          onChange={(e) =>
            setFilters({ ...filters, status: e.target.value as 'success' | 'error' | '' })
          }
          style={{
            height: 36,
            padding: '0 12px',
            borderRadius: 6,
            border: '1px solid #cbd5e1',
            background: '#fff',
            fontSize: 14,
          }}
        >
          <option value="">All</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
        </select>
      </div>

      {isLoading ? (
        <div
          style={{
            height: 600,
            background: '#e2e8f0',
            borderRadius: 8,
            animation: 'pulse 2s infinite',
          }}
        />
      ) : isError ? (
        <div
          style={{
            padding: 24,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            color: '#991b1b',
            fontSize: 13,
          }}
        >
          <strong>Failed to load logs:</strong>{' '}
          {error instanceof ApiException ? error.message : 'Unknown error'}
        </div>
      ) : (
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            overflow: 'auto',
            maxHeight: 600,
          }}
        >
          <table>
            <thead
              style={{
                background: '#f8fafc',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <tr>
                <Th>Time</Th>
                <Th>Model</Th>
                <Th>User</Th>
                <Th align="right">Tokens</Th>
                <Th align="right">Cost</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {rows?.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 32,
                      textAlign: 'center',
                      fontSize: 13,
                      color: '#64748b',
                    }}
                  >
                    No logs found
                  </td>
                </tr>
              ) : (
                rows?.map((log) => (
                  <tr
                    key={log.request_id}
                    style={{ borderTop: '1px solid #e2e8f0', cursor: 'pointer' }}
                    onClick={() => {
                      // Pass the active date range to the detail page so the
                      // worker can resolve historical log rows.
                      const params = new URLSearchParams();
                      if (filters.start_date) params.set('start_date', filters.start_date);
                      if (filters.end_date) params.set('end_date', filters.end_date);
                      const qs = params.toString();
                      navigate(
                        `/logs/${encodeURIComponent(log.request_id)}${qs ? `?${qs}` : ''}`,
                      );
                    }}
                  >
                    <Td>
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                    </Td>
                    <Td>{log.model ?? '-'}</Td>
                    <Td>
                      <span style={{ color: '#64748b' }}>{log.user ?? '-'}</span>
                    </Td>
                    <Td align="right">{log.total_tokens ?? '-'}</Td>
                    <Td align="right">
                      {typeof log.cost === 'number' ? `$${log.cost.toFixed(4)}` : '-'}
                    </Td>
                    <Td>
                      <span
                        style={{
                          color: log.status === 'success' ? '#16a34a' : '#dc2626',
                        }}
                      >
                        {log.status ?? '-'}
                      </span>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '12px 16px',
        textAlign: align ?? 'left',
        fontSize: 12,
        fontWeight: 500,
        color: '#475569',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      style={{
        padding: '8px 12px',
        textAlign: align ?? 'left',
        fontSize: 12,
      }}
    >
      {children}
    </td>
  );
}
